import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
  conditions?: PermissionCondition[];
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface PermissionCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'in' | 'not_in' | 'contains' | 'starts_with' | 'ends_with';
  value: any;
  logic?: 'AND' | 'OR';
}

export interface Role {
  id: string;
  name: string;
  description: string;
  level: number; // Higher number = more senior role
  permissions: Permission[];
  is_system: boolean;
  organization_id?: string;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role_id: string;
  organization_id?: string;
  department_id?: string;
  assigned_by: string;
  assigned_at: string;
  expires_at?: string;
  is_active: boolean;
}

export interface ResourceAccess {
  id: string;
  user_id: string;
  resource_type: string;
  resource_id: string;
  access_level: 'none' | 'read' | 'write' | 'admin' | 'owner';
  granted_by: string;
  granted_at: string;
  expires_at?: string;
  conditions?: Record<string, any>;
}

export interface PermissionRequest {
  id: string;
  user_id: string;
  requested_permission: string;
  resource_type: string;
  resource_id?: string;
  justification: string;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  requested_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  expires_at?: string;
}

export interface AccessPolicy {
  id: string;
  name: string;
  description: string;
  resource_type: string;
  conditions: PermissionCondition[];
  default_access_level: 'none' | 'read' | 'write' | 'admin';
  approval_required: boolean;
  approval_workflow?: string[];
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

class PermissionService {
  // Permission Management
  async createPermission(
    permissionData: Omit<Permission, 'id' | 'created_at' | 'updated_at'>
  ): Promise<Permission> {
    const { data, error } = await supabase
      .from('permissions')
      .insert(permissionData)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getPermissions(): Promise<Permission[]> {
    const { data, error } = await supabase
      .from('permissions')
      .select('*')
      .order('resource', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async updatePermission(id: string, updates: Partial<Permission>): Promise<Permission> {
    const { data, error } = await supabase
      .from('permissions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Role Management
  async createRole(
    roleData: Omit<Role, 'id' | 'created_at' | 'updated_at' | 'permissions'>
  ): Promise<Role> {
    const { data, error } = await supabase.from('roles').insert(roleData).select().single();

    if (error) throw error;
    return data;
  }

  async getRoles(organizationId?: string): Promise<Role[]> {
    let query = supabase.from('roles').select(`
        *,
        role_permissions!inner(
          permissions(*)
        )
      `);

    if (organizationId) {
      query = query.or(`organization_id.eq.${organizationId},organization_id.is.null`);
    }

    const { data, error } = await query.order('level', { ascending: false });

    if (error) throw error;

    // Transform the data to include permissions array
    return (data || []).map((role) => ({
      ...role,
      permissions: role.role_permissions?.map((rp: any) => rp.permissions) || [],
    }));
  }

  async updateRole(id: string, updates: Partial<Role>): Promise<Role> {
    const { data, error } = await supabase
      .from('roles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async assignPermissionToRole(roleId: string, permissionId: string): Promise<void> {
    const { error } = await supabase.from('role_permissions').insert({
      role_id: roleId,
      permission_id: permissionId,
    });

    if (error) throw error;
  }

  async removePermissionFromRole(roleId: string, permissionId: string): Promise<void> {
    const { error } = await supabase
      .from('role_permissions')
      .delete()
      .eq('role_id', roleId)
      .eq('permission_id', permissionId);

    if (error) throw error;
  }

  // User Role Assignment
  async assignUserRole(assignmentData: Omit<UserRole, 'id' | 'assigned_at'>): Promise<UserRole> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_roles')
      .insert({
        ...assignmentData,
        assigned_by: user.id,
        assigned_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getUserRoles(userId: string): Promise<UserRole[]> {
    const { data, error } = await supabase
      .from('user_roles')
      .select(
        `
        *,
        roles(*)
      `
      )
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) throw error;
    return data || [];
  }

  async removeUserRole(userRoleId: string): Promise<void> {
    const { error } = await supabase
      .from('user_roles')
      .update({ is_active: false })
      .eq('id', userRoleId);

    if (error) throw error;
  }

  // Permission Checking
  async checkPermission(userId: string, permission: string, resourceId?: string): Promise<boolean> {
    try {
      // Get user's roles and their permissions
      const userRoles = await this.getUserRoles(userId);
      const roleIds = userRoles.map((ur) => ur.role_id);

      if (roleIds.length === 0) return false;

      // Check if any role has the required permission
      const { data: rolePermissions } = await supabase
        .from('role_permissions')
        .select(
          `
          permissions(*)
        `
        )
        .in('role_id', roleIds);

      if (!rolePermissions) return false;

      const permissions = rolePermissions.map((rp) => rp.permissions).flat();
      const hasPermission = permissions.some(
        (p: any) => p.name === permission && this.evaluatePermissionConditions(p, { resourceId })
      );

      if (hasPermission) return true;

      // Check resource-specific access
      if (resourceId) {
        const hasResourceAccess = await this.checkResourceAccess(userId, permission, resourceId);
        if (hasResourceAccess) return true;
      }

      return false;
    } catch (error) {
      logError('Permission check failed:', error);
      return false;
    }
  }

  private evaluatePermissionConditions(permission: any, context: { resourceId?: string }): boolean {
    if (!permission.conditions || permission.conditions.length === 0) {
      return true;
    }

    // Simple condition evaluation - in practice this would be more sophisticated
    return permission.conditions.every((condition: PermissionCondition) => {
      switch (condition.field) {
        case 'resource_id':
          return this.evaluateCondition(context.resourceId, condition.operator, condition.value);
        default:
          return true;
      }
    });
  }

  private evaluateCondition(value: any, operator: string, expectedValue: any): boolean {
    switch (operator) {
      case 'equals':
        return value === expectedValue;
      case 'not_equals':
        return value !== expectedValue;
      case 'in':
        return Array.isArray(expectedValue) && expectedValue.includes(value);
      case 'not_in':
        return Array.isArray(expectedValue) && !expectedValue.includes(value);
      case 'contains':
        return String(value).includes(String(expectedValue));
      case 'starts_with':
        return String(value).startsWith(String(expectedValue));
      case 'ends_with':
        return String(value).endsWith(String(expectedValue));
      default:
        return false;
    }
  }

  // Resource Access Management
  async grantResourceAccess(
    accessData: Omit<ResourceAccess, 'id' | 'granted_at'>
  ): Promise<ResourceAccess> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('resource_access')
      .insert({
        ...accessData,
        granted_by: user.id,
        granted_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async checkResourceAccess(userId: string, action: string, resourceId: string): Promise<boolean> {
    const { data: access } = await supabase
      .from('resource_access')
      .select('*')
      .eq('user_id', userId)
      .eq('resource_id', resourceId)
      .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString())
      .single();

    if (!access) return false;

    // Map actions to access levels
    const actionAccessMap: Record<string, string[]> = {
      read: ['read', 'write', 'admin', 'owner'],
      write: ['write', 'admin', 'owner'],
      delete: ['admin', 'owner'],
      admin: ['admin', 'owner'],
      owner: ['owner'],
    };

    const requiredLevels = actionAccessMap[action] || ['owner'];
    return requiredLevels.includes(access.access_level);
  }

  async revokeResourceAccess(userId: string, resourceId: string): Promise<void> {
    const { error } = await supabase
      .from('resource_access')
      .delete()
      .eq('user_id', userId)
      .eq('resource_id', resourceId);

    if (error) throw error;
  }

  // Permission Requests
  async createPermissionRequest(
    requestData: Omit<PermissionRequest, 'id' | 'requested_at' | 'status'>
  ): Promise<PermissionRequest> {
    const { data, error } = await supabase
      .from('permission_requests')
      .insert({
        ...requestData,
        status: 'pending',
        requested_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    // Notify approvers
    await this.notifyApprovers(data);

    return data;
  }

  async reviewPermissionRequest(
    requestId: string,
    decision: 'approved' | 'rejected',
    reviewNotes?: string
  ): Promise<PermissionRequest> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('permission_requests')
      .update({
        status: decision,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        review_notes: reviewNotes,
      })
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;

    // If approved, grant the permission
    if (decision === 'approved') {
      await this.grantRequestedPermission(data);
    }

    return data;
  }

  private async notifyApprovers(request: PermissionRequest): Promise<void> {
    // Get users who can approve this type of request
    const { data: approvers } = await supabase
      .from('user_roles')
      .select(
        `
        user_id,
        user_profiles(email, first_name, last_name)
      `
      )
      .eq('is_active', true);

    // In a real implementation, send notifications
  }

  private async grantRequestedPermission(request: PermissionRequest): Promise<void> {
    if (request.resource_id) {
      // Grant resource-specific access
      await this.grantResourceAccess({
        user_id: request.user_id,
        resource_type: request.resource_type,
        resource_id: request.resource_id,
        access_level: this.mapPermissionToAccessLevel(request.requested_permission),
        granted_by: request.reviewed_by!,
        expires_at: request.expires_at,
      });
    } else {
      // This would require creating a temporary role or permission
    }
  }

  private mapPermissionToAccessLevel(permission: string): ResourceAccess['access_level'] {
    if (permission.includes('admin')) return 'admin';
    if (
      permission.includes('write') ||
      permission.includes('update') ||
      permission.includes('delete')
    )
      return 'write';
    return 'read';
  }

  // Access Policies
  async createAccessPolicy(
    policyData: Omit<AccessPolicy, 'id' | 'created_at' | 'updated_at'>
  ): Promise<AccessPolicy> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('access_policies')
      .insert({
        ...policyData,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getAccessPolicies(resourceType?: string): Promise<AccessPolicy[]> {
    let query = supabase.from('access_policies').select('*').eq('is_active', true);

    if (resourceType) {
      query = query.eq('resource_type', resourceType);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async evaluateAccessPolicy(
    userId: string,
    resourceType: string,
    resourceId: string
  ): Promise<string> {
    const policies = await this.getAccessPolicies(resourceType);

    for (const policy of policies) {
      if (this.evaluatePolicyConditions(policy.conditions, { userId, resourceId })) {
        return policy.default_access_level;
      }
    }

    return 'none';
  }

  private evaluatePolicyConditions(conditions: PermissionCondition[], context: any): boolean {
    if (!conditions || conditions.length === 0) return true;

    return conditions.every((condition) => {
      const value = this.getContextValue(condition.field, context);
      return this.evaluateCondition(value, condition.operator, condition.value);
    });
  }

  private getContextValue(field: string, context: any): any {
    const fieldParts = field.split('.');
    let value = context;

    for (const part of fieldParts) {
      value = value?.[part];
    }

    return value;
  }

  // Advanced Permission Features
  async getEffectivePermissions(userId: string): Promise<any> {
    const userRoles = await this.getUserRoles(userId);
    const roleIds = userRoles.map((ur) => ur.role_id);

    // Get all permissions from roles
    const { data: rolePermissions } = await supabase
      .from('role_permissions')
      .select(
        `
        permissions(*)
      `
      )
      .in('role_id', roleIds);

    // Get resource-specific access
    const { data: resourceAccess } = await supabase
      .from('resource_access')
      .select('*')
      .eq('user_id', userId)
      .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

    const permissions = rolePermissions?.map((rp) => rp.permissions).flat() || [];
    const resources = resourceAccess || [];

    return {
      role_permissions: permissions,
      resource_access: resources,
      roles: userRoles.map((ur) => ur.roles),
      effective_permissions: this.consolidatePermissions(permissions, resources),
    };
  }

  private consolidatePermissions(permissions: any[], resourceAccess: ResourceAccess[]): any {
    const consolidated: Record<string, any> = {};

    // Add role-based permissions
    permissions.forEach((permission) => {
      const key = `${permission.resource}:${permission.action}`;
      consolidated[key] = {
        type: 'role',
        permission: permission.name,
        conditions: permission.conditions,
      };
    });

    // Add resource-specific access
    resourceAccess.forEach((access) => {
      const key = `${access.resource_type}:${access.resource_id}`;
      consolidated[key] = {
        type: 'resource',
        access_level: access.access_level,
        expires_at: access.expires_at,
      };
    });

    return consolidated;
  }

  // Permission Analytics
  async getPermissionAnalytics(): Promise<any> {
    const [users, roles, permissions, requests] = await Promise.all([
      supabase.from('user_profiles').select('id'),
      supabase.from('roles').select('*'),
      supabase.from('permissions').select('*'),
      supabase.from('permission_requests').select('*'),
    ]);

    const userData = users.data || [];
    const roleData = roles.data || [];
    const permissionData = permissions.data || [];
    const requestData = requests.data || [];

    return {
      total_users: userData.length,
      total_roles: roleData.length,
      total_permissions: permissionData.length,
      pending_requests: requestData.filter((r) => r.status === 'pending').length,
      approved_requests: requestData.filter((r) => r.status === 'approved').length,
      rejected_requests: requestData.filter((r) => r.status === 'rejected').length,
      roles_by_level: this.groupBy(roleData, 'level'),
      permissions_by_resource: this.groupBy(permissionData, 'resource'),
      requests_by_urgency: this.groupBy(requestData, 'urgency'),
      request_approval_rate:
        requestData.length > 0
          ? Math.round(
              (requestData.filter((r) => r.status === 'approved').length / requestData.length) * 100
            )
          : 0,
    };
  }

  private groupBy(array: any[], key: string): Record<string, number> {
    return array.reduce((acc, item) => {
      const group = item[key] || 'unknown';
      acc[group] = (acc[group] || 0) + 1;
      return acc;
    }, {});
  }

  // Compliance and Auditing
  async getPermissionAuditTrail(userId?: string, resourceId?: string): Promise<any[]> {
    let query = supabase
      .from('audit_logs')
      .select('*')
      .in('action', ['permission_granted', 'permission_revoked', 'role_assigned', 'role_removed']);

    if (userId) query = query.eq('metadata->>target_user_id', userId);
    if (resourceId) query = query.eq('resource_id', resourceId);

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async validatePermissionCompliance(): Promise<any> {
    // Check for users without roles
    const { data: usersWithoutRoles } = await supabase
      .from('user_profiles')
      .select(
        `
        id, email,
        user_roles!left(id)
      `
      )
      .is('user_roles.id', null);

    // Check for expired roles
    const { data: expiredRoles } = await supabase
      .from('user_roles')
      .select('*')
      .lt('expires_at', new Date().toISOString())
      .eq('is_active', true);

    // Check for overprivileged users (users with admin roles)
    const { data: adminUsers } = await supabase
      .from('user_roles')
      .select(
        `
        user_id,
        roles!inner(name, level)
      `
      )
      .gte('roles.level', 90)
      .eq('is_active', true);

    return {
      users_without_roles: usersWithoutRoles || [],
      expired_roles: expiredRoles || [],
      admin_users: adminUsers || [],
      compliance_issues: [
        ...(usersWithoutRoles || []).map((u) => ({
          type: 'missing_role',
          user_id: u.id,
          description: 'User has no assigned roles',
        })),
        ...(expiredRoles || []).map((r) => ({
          type: 'expired_role',
          user_id: r.user_id,
          description: 'User has expired role assignment',
        })),
      ],
    };
  }
}

export const permissionService = new PermissionService();
